import { useEffect } from 'react';
import { Alert, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';

import { dashboardApi } from '../api/dashboard';
import { buildAttention } from '../features/dashboard/attention';
import { useClaimsUnreadCount } from '../hooks/useClaimsUnreadCount';

import { StatusBar } from '../features/dashboard/components/StatusBar';
import { KpiCards } from '../features/dashboard/components/KpiCards';
import { PipelineFlow } from '../features/dashboard/components/PipelineFlow';
import { ChambersGrid } from '../features/dashboard/components/ChambersGrid';
import { RecentMovementsFeed } from '../features/dashboard/components/RecentMovementsFeed';
import { StocksWidget } from '../features/dashboard/components/StocksWidget';
import { DeficitAlert } from '../features/dashboard/components/DeficitAlert';
import { FinanceWidget } from '../features/dashboard/components/FinanceWidget';
import { OrdersProgressWidget } from '../features/dashboard/components/OrdersProgressWidget';
import { ProductionActivityWidget } from '../features/dashboard/components/ProductionActivityWidget';

import '../features/dashboard/dashboard.css';

const DashboardPage = () => {
  const { data, isLoading, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardApi.summary(),
    refetchInterval: 60_000, // auto-refresh every minute
  });
  const { data: claimsUnread = 0 } = useClaimsUnreadCount();

  useEffect(() => {
    document.title = 'DNW · Огляд';
  }, []);

  if (isLoading || !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 96 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Не вдалося завантажити дашборд"
        description={String(error)}
        action={<button onClick={() => refetch()}>Повторити</button>}
      />
    );
  }

  const attention = buildAttention(
    data.chambers,
    data.deficit,
    data.finance,
    data.versions_without_norm,
  );

  return (
    <div className="dash-page">
      <StatusBar pulse={data.system_pulse} updatedAt={dataUpdatedAt} chambers={data.chambers} />

      <KpiCards
        kpi={data.kpi}
        headline={data.headline}
        attention={attention}
        claimsCount={claimsUnread}
      />

      <PipelineFlow pipeline={data.pipeline} />

      <div className="dash-cols-main">
        <ChambersGrid chambers={data.chambers} />
        <RecentMovementsFeed movements={data.recent_movements} />
      </div>

      <div className="dash-cols-half">
        <StocksWidget pipeline={data.pipeline} />
        <DeficitAlert items={attention} />
      </div>

      <OrdersProgressWidget overview={data.orders_overview} />

      <ProductionActivityWidget />

      <FinanceWidget finance={data.finance} />
    </div>
  );
};

export default DashboardPage;
